import hls_player_mixin from "./live_hls_player_mixin";
import camera_proxy_mixin from "./live_camera_proxy_mixin";

export default {
  name: "LivePropertyFeed",
  mixins: [hls_player_mixin, camera_proxy_mixin],
  props: {
    feed: {
      type: Object,
      default: null
    },
    locationName: {
      type: String,
      default: "Live Property"
    },
    coordinates: {
      type: Object,
      default: null
    },
    selectedLocation: {
      type: Object,
      default: null
    }
  },
  data() {
    return ({
      is_fullscreen: false
    });
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.handle_fullscreen_change);
    this.prepare_feed();
  },
  beforeUnmount() {
    document.removeEventListener("fullscreenchange", this.handle_fullscreen_change);
    this.clear_warmup_timer();
    this.clear_reconnect_timer();
    this.destroy_hls_player();
    this.stop_hls_proxy();
  },
  watch: {
    feed(newFeed, oldFeed) {
      const new_url = newFeed?.url || "";
      const old_url = oldFeed?.url || "";

      if (new_url !== old_url) {
        this.stop_hls_proxy(oldFeed);
        this.refresh_feed();
      }
    }
  },
  computed: {
    stream_url() {
      return this.feed?.url || "";
    },
    camera_label() {
      return this.feed?.label || "Live camera";
    },
    render_mode() {
      const configured_type = String(this.feed?.type || "auto").toLowerCase();
      if (configured_type === "hls") {
        return "video";
      }

      if (["video", "iframe", "image"].includes(configured_type)) {
        return configured_type;
      }

      const url = this.stream_url.toLowerCase();
      if (url.includes("youtube.com") || url.includes("youtu.be") || url.includes("vimeo.com")) {
        return "iframe";
      }

      if (/\.(mp4|webm|ogg|m3u8)(\?|#|$)/.test(url)) {
        return "video";
      }

      return "image";
    },
    selected_location_name() {
      return this.selectedLocation?.name || this.locationName;
    },
    selected_coordinates() {
      return this.selectedLocation?.coordinates || this.coordinates;
    },
    coordinate_label() {
      if (!this.selected_coordinates) return "Coordinates unavailable";

      const lat = Number(this.selected_coordinates.lat);
      const lng = Number(this.selected_coordinates.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return "Coordinates unavailable";
      }

      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    },
    live_feed_src() {
      if (!this.stream_url) return "";

      const separator = this.stream_url.includes("?") ? "&" : "?";
      return `${this.stream_url}${separator}t=${this.refresh_key}`;
    },
    video_key() {
      return this.is_hls_feed() ? this.stream_url : this.live_feed_src;
    },
    should_show_video() {
      return Boolean(this.stream_url && this.render_mode === "video" && (!this.image_failed || this.is_hls_feed()));
    },
    should_show_empty_state() {
      if (this.is_loading) return false;
      return !this.stream_url || this.image_failed;
    },
    feed_status_title() {
      if (this.is_loading) return "Starting live footage";
      return "Live footage unavailable";
    },
    feed_status_message() {
      if (this.status_message) return this.status_message;
      return "Check the camera stream or network access.";
    }
  },
  methods: {
    async toggle_fullscreen() {
      const target = this.$refs.live_feed_card;
      if (!target) return;

      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
          return;
        }

        await target.requestFullscreen();
      } catch (error) {
        console.warn("Unable to toggle live feed fullscreen.", error);
      }
    },
    handle_fullscreen_change() {
      this.is_fullscreen = document.fullscreenElement === this.$refs.live_feed_card;
    }
  }
};
